import { Users } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { AcademicCalendarView } from '@/features/institute/calendar/components/AcademicCalendarView';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * The academic calendar, for the parent of the child on screen.
 *
 * Reads `GET /parent/children/:uuid/calendar` — the office's own planner, read
 * through the same service and narrowed by the server to entries addressed to
 * parents and to this child's stage — and draws it with the office's own
 * `AcademicCalendarView`, read-only. Holidays come from the same table the
 * attendance register reads, so a day shown shut here is shut on the register.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentCalendarPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.calendar(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  // Switching children refetches; until the new calendar lands, the old one
  // was narrowed for somebody else's class and must not be drawn under this name.
  const calendar = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your school's exam weeks, events and holidays appear here."
        />
      </div>
    );
  }

  return (
    <AcademicCalendarView
      // Remounted per child: the month being browsed and any open entry belong
      // to the calendar that was on screen, not the next child's.
      key={child.uuid}
      data={calendar}
      isLoading={isLoading || (!calendar && !error)}
      error={error}
      onRetry={refetch}
      title="Academic calendar"
      description={`Exams, events, meetings and holidays for ${child.fullName} in ${child.className}.`}
      emptyDescription="Your school has not put anything on this session's calendar for parents yet. Exam weeks, meetings and holidays appear here as soon as the school office adds them."
      className={PARENT_PALETTE}
    />
  );
}
